import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { getLenis, prefersReducedMotion, destroyLenis } from '@/lib/lenis';
import { registerGsap } from '@/lib/gsap';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';

// Defer the global ParticleField (Three.js) until after first paint. It's a
// background atmosphere effect — non-essential for interaction. Lazy import
// keeps the three-vendor chunk out of the initial JS payload.
const ParticleField = lazy(() =>
  import('@/components/three/ParticleField').then((m) => ({
    default: m.ParticleField,
  }))
);
import { ToastViewport } from '@/components/ui/Toast';
import { CartDrawer } from '@/components/cart';
import { QuickViewModal } from '@/components/product';
import { CustomCursor } from './CustomCursor';
import { Navbar } from './Navbar';
import { MobileNav } from './MobileNav';
import { Footer } from './Footer';
import { PageTransition } from './PageTransition';
import { InstallPrompt } from './InstallPrompt';

interface RootLayoutProps {
  /** Routed content. Task 18 will pass <Outlet /> here. */
  children: ReactNode;
  /** When true, the page transition curtain wraps `children`. Default true. */
  withPageTransition?: boolean;
  /** When true, the global Footer renders. Disable on full-bleed pages (e.g.
   *  checkout success). Default true. */
  withFooter?: boolean;
}

/**
 * Top-level visual chrome — the only thing above this in the tree is the
 * Router + QueryClient + StrictMode (wired in main.tsx / App.tsx by Task 18).
 *
 * Responsibilities:
 *   1. Boot Lenis singleton + register GSAP plugins (synced).
 *   2. Mount the global `ParticleField` (z-index: -1, always behind content).
 *   3. Mount `CustomCursor` (desktop pointer replacement).
 *   4. Render `Navbar` + `MobileNav` (always on top, z >= 60).
 *   5. Wrap routed children in a `PageTransition` for the magenta curtain.
 *   6. Render `Footer` at the bottom.
 *
 * Stacking order (z-index):
 *   -1   ParticleField (canvas)
 *    0   Page content (PageTransition wrapped)
 *   60   Navbar
 *  100+  MobileNav (overlay + panel)
 * 1500   PageTransition curtain
 * 2000+  CustomCursor
 */
export function RootLayout({
  children,
  withPageTransition = true,
  withFooter = true,
}: RootLayoutProps) {
  // Boot Lenis + GSAP once on mount. Both are idempotent.
  useEffect(() => {
    const lenis = getLenis();
    registerGsap();
    // If reduced-motion is set, lenis is null and we let the browser scroll
    // natively. Otherwise the GSAP ticker (registered by registerGsap) drives
    // the lenis raf, so we don't need a separate raf loop here.
    void lenis;

    return () => {
      // We intentionally do NOT destroy Lenis on unmount — RootLayout is the
      // app root, so unmount means navigating away from the SPA. If a hot
      // reload scenario triggers it, destroyLenis() guards against orphaned
      // listeners.
      if (import.meta.hot) {
        import.meta.hot.dispose(() => destroyLenis());
      }
    };
  }, []);

  const reduceMotion = prefersReducedMotion();

  return (
    <ErrorBoundary>
      {/* Global WebGL background — fixed, behind everything. Lazy + null fallback
          so the page never blocks on Three.js boot. */}
      {!reduceMotion && (
        <Suspense fallback={null}>
          <ParticleField />
        </Suspense>
      )}

      {/* Velvet Cosmos vignette — keeps card backgrounds readable regardless
          of where the user has scrolled. Sits above particles, below content. */}
      <div className="qxk-vignette" aria-hidden />


      {/* Desktop pointer replacement — auto no-op on touch + reduced-motion. */}
      {!reduceMotion && <CustomCursor />}

      {/* Skip-to-content for keyboard users (PRD §14 a11y checklist). */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[2500] focus:rounded-md focus:bg-[color:var(--color-brand-magenta)] focus:px-4 focus:py-2 focus:text-white focus:shadow-lg"
      >
        Skip to content
      </a>

      <Navbar />
      <MobileNav />

      <main
        id="main-content"
        className="relative pt-[72px]"
        style={{ minHeight: 'calc(100svh - 72px)' }}
      >
        {withPageTransition ? (
          <PageTransition>{children}</PageTransition>
        ) : (
          children
        )}
      </main>

      {withFooter && <Footer />}

      {/* Global overlays — mounted once at the layout root. */}
      <CartDrawer />
      <QuickViewModal />
      <ToastViewport />
      <InstallPrompt />
    </ErrorBoundary>
  );
}
